import { apiPath } from '@/redux/apiPath';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';

/**
 * Glyph images are served from an authenticated endpoint, so they cannot be loaded by assigning
 * the url to `img.src` - the browser would not attach the Bearer token. Instead the bytes are
 * fetched through the api client (which attaches and refreshes the token) and turned into an
 * object url.
 *
 * The same glyph is usually displayed in many places, so the results are cached per glyph id. That
 * way the image is fetched once and decoded once, no matter how many elements use it. The object
 * url is deliberately kept alive for as long as the entry is cached.
 */
const glyphImageUrls = new Map<number, Promise<string>>();
const glyphImages = new Map<number, Promise<HTMLImageElement>>();

const fetchGlyphImageUrl = async (glyphId: number): Promise<string> => {
  const { data } = await axiosInstanceNewAPI.get<Blob>(apiPath.getGlyphImage(glyphId), {
    responseType: 'blob',
  });

  return URL.createObjectURL(data);
};

const decodeImage = (objectUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = (): void => resolve(image);
    image.onerror = (): void => reject(new Error('Failed to decode glyph image'));
    image.src = objectUrl;
  });

/**
 * Object url of the glyph image, ready to be used as an `src` of an `img` element. Resolves as soon
 * as the bytes are fetched - it does not wait for the browser to decode them.
 */
export const getGlyphImageUrl = (glyphId: number): Promise<string> => {
  const cachedUrl = glyphImageUrls.get(glyphId);
  if (cachedUrl) {
    return cachedUrl;
  }

  const url = fetchGlyphImageUrl(glyphId).catch(error => {
    // a failed request must not be cached, so that the next attempt can retry
    glyphImageUrls.delete(glyphId);
    throw error;
  });
  glyphImageUrls.set(glyphId, url);

  return url;
};

/** Decoded glyph image, ready to be used as a source of an OpenLayers icon or a canvas draw. */
export const getGlyphImage = (glyphId: number): Promise<HTMLImageElement> => {
  const cachedImage = glyphImages.get(glyphId);
  if (cachedImage) {
    return cachedImage;
  }

  const image = getGlyphImageUrl(glyphId)
    .then(decodeImage)
    .catch(error => {
      glyphImages.delete(glyphId);
      throw error;
    });
  glyphImages.set(glyphId, image);

  return image;
};

export const clearGlyphImagesCache = (): void => {
  glyphImageUrls.forEach(url => {
    url.then(objectUrl => URL.revokeObjectURL(objectUrl)).catch(() => {});
  });
  glyphImageUrls.clear();
  glyphImages.clear();
};
